import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ErrorHandler from '../../utils/errorHandler';

const CreateChama = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    group_type: 'general',
    contribution_frequency: 'monthly',
    contribution_amount: '',
    meeting_day: '',
    late_penalty: '',
  });

  const groupTypes = [
    {
      value: 'merry_go_round',
      label: 'Merry-Go-Round',
      description: 'Members contribute regularly and receive lump sums in rotation',
      icon: '🔄',
    },
    {
      value: 'investment',
      label: 'Investment Club',
      description: 'Pool funds for collective investments in stocks, real estate, or business',
      icon: '📈',
    },
    {
      value: 'welfare',
      label: 'Welfare Group',
      description: 'Support members during emergencies, medical needs, or bereavements',
      icon: '🤝',
    },
    {
      value: 'fundraising',
      label: 'Fundraising Campaign',
      description: 'Temporary group for specific fundraising goals or projects',
      icon: '🎯',
    },
    {
      value: 'sacco',
      label: 'SACCO',
      description: 'Savings and Credit Cooperative with formal loan structures',
      icon: '🏦',
    },
    {
      value: 'table_banking',
      label: 'Table Banking',
      description: 'Members borrow from pooled funds during meetings with quick approval',
      icon: '💰',
    },
    {
      value: 'general',
      label: 'General Chama',
      description: 'Flexible group for mixed activities and purposes',
      icon: '👥',
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setErrors({});

      const response = await api.post('/chamas', formData);

      // Success - navigate to the new chama
      navigate(`/chamas/${response.data.id}`);
    } catch (error) {
      // Check if it&apos;s a subscription error (403)
      if (error.response?.status === 403) {
        const message =
          error.response?.data?.message || 'You need an active subscription to create a group';

        // Show error briefly then redirect to pricing page
        setErrors({ general: message });

        setTimeout(() => {
          navigate('/#pricing', {
            state: {
              message,
              returnTo: '/chamas/create',
            },
          });
        }, 2000);

        setLoading(false);
        return;
      }

      const errorMessage = ErrorHandler.getErrorMessage(error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors border border-white/10"
        >
          <svg
            className="w-6 h-6 text-[#d8d2c5]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">Create New Chama</h1>
          <p className="mt-1 text-[#d8d2c5]">Set up your savings group and invite members</p>
        </div>
      </div>

      {/* Error Alert */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex gap-3">
            <svg
              className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-800">{errors.general}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Group Type Selection */}
        <Card title="Choose Group Type" subtitle="Select the type that best fits your chama">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groupTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, group_type: type.value }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.group_type === type.value
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{type.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{type.label}</h3>
                    <p className="text-sm text-[#d8d2c5]">{type.description}</p>
                  </div>
                  {formData.group_type === type.value && (
                    <svg
                      className="w-6 h-6 text-emerald-500 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.group_type && <p className="mt-2 text-sm text-red-400">{errors.group_type[0]}</p>}
        </Card>

        {/* Basic Information */}
        <Card title="Basic Information" subtitle="Tell us about your chama">
          <div className="space-y-4">
            <Input
              label="Chama Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name?.[0]}
              placeholder="e.g., Savings Champions"
              required
            />

            <div>
              <div className="block text-sm font-medium text-white mb-2">Description</div>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#d8d2c5]/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
                placeholder="What's the purpose of this chama?"
              />
              {errors.description && (
                <p className="mt-2 text-sm text-red-400">{errors.description[0]}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Contribution Settings */}
        <Card title="Contribution Settings" subtitle="Define how contributions work">
          <div className="space-y-4">
            <div>
              <div className="block text-sm font-medium text-white mb-2">
                Contribution Frequency
              </div>
              <select
                name="contribution_frequency"
                value={formData.contribution_frequency}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>

            <Input
              label="Contribution Amount (KES)"
              name="contribution_amount"
              type="number"
              value={formData.contribution_amount}
              onChange={handleChange}
              error={errors.contribution_amount?.[0]}
              placeholder="e.g., 1000"
              helpText="Amount each member contributes per period"
            />

            <Input
              label="Late Penalty (KES)"
              name="late_penalty"
              type="number"
              value={formData.late_penalty}
              onChange={handleChange}
              error={errors.late_penalty?.[0]}
              placeholder="e.g., 50"
              helpText="Optional penalty for late contributions"
            />
          </div>
        </Card>

        {/* Meeting Settings */}
        <Card title="Meeting Settings" subtitle="When does your chama meet?">
          <div className="space-y-4">
            <div>
              <div className="block text-sm font-medium text-gray-700 mb-2">Meeting Day</div>
              <select
                name="meeting_day"
                value={formData.meeting_day}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              >
                <option value="">Select a day</option>
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
              {errors.meeting_day && (
                <p className="mt-2 text-sm text-red-600">{errors.meeting_day[0]}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            fullWidth
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            loading={loading}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            }
          >
            Create Chama
          </Button>
        </div>
      </form>

      {/* Info Box */}
      <Card>
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6 text-sky-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">What happens next?</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Your chama will be created with you as the admin</li>
              <li>• You can invite members via email or phone number</li>
              <li>• Set up contribution schedules and loan policies</li>
              <li>• Start tracking contributions and meetings</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CreateChama;
